/**
 * @spec docs/specs/features/staking-position-ui.md#wallet-integration
 * Wallet status indicator with dropdown to switch between connected wallets
 */
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { routes } from '@/lib/routes';
import { useSelectedWallet } from '@/hooks/useSelectedWallet';
import { WalletAddress } from '@/components/ui/WalletAddress';
import { Dropdown, type DropdownItem } from '@/components/ui/Dropdown';
import WalletIcon from '@/assets/icons/wallet.svg?react';
import CheckIcon from '@/assets/icons/check.svg?react';

export interface WalletStatusProps {
  /** Custom className for the container */
  className?: string;
}

/**
 * Get human-readable label for wallet client type
 */
function getWalletTypeLabel(clientType: string): string {
  const labels: Record<string, string> = {
    privy: 'Privy Wallet',
    metamask: 'MetaMask',
    coinbase_wallet: 'Coinbase Wallet',
    wallet_connect: 'WalletConnect',
    rainbow: 'Rainbow',
    phantom: 'Phantom',
  };
  return labels[clientType] ?? clientType;
}

/**
 * Displays the connected wallet with dropdown to switch wallets
 *
 * Features:
 * - Shows currently selected wallet address
 * - Dropdown with all connected wallets
 * - Each wallet shows address and type (Privy, MetaMask, etc.)
 * - Clicking a wallet selects it and navigates to its address page
 * - Click outside or Escape closes dropdown
 */
export function WalletStatus({ className }: WalletStatusProps) {
  const navigate = useNavigate();
  const { address, wallets, setSelectedAddress } = useSelectedWallet();

  // Transform wallets into dropdown items
  const dropdownItems = useMemo((): DropdownItem<string>[] => {
    return wallets.map((wallet) => {
      const isSelected = wallet.address.toLowerCase() === address?.toLowerCase();
      return {
        id: wallet.address,
        label: <WalletAddress address={wallet.address} chars={6} />,
        secondaryLabel: getWalletTypeLabel(wallet.walletClientType),
        trailing: isSelected ? <CheckIcon className="w-4 h-4 text-accent-purple" /> : undefined,
      };
    });
  }, [wallets, address]);

  const handleWalletSelect = useCallback(
    (walletAddress: string) => {
      setSelectedAddress(walletAddress);
      navigate(routes.address(walletAddress));
    },
    [setSelectedAddress, navigate]
  );

  if (!address) {
    return null;
  }

  const trigger = (
    <>
      <WalletIcon className="h-4 w-4 text-accent-purple" />
      <WalletAddress address={address} className="text-text-primary" />
    </>
  );

  return (
    <Dropdown
      items={dropdownItems}
      selectedId={address}
      onSelect={handleWalletSelect}
      trigger={trigger}
      className={className}
      triggerClassName={cn(
        'rounded-lg bg-bg-secondary px-3 py-2 border border-border',
        'transition-colors',
        wallets.length > 1 && 'hover:border-border-hover'
      )}
      ariaLabel="Select wallet"
    />
  );
}
